import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import WorkstreamBreadcrumb from '../components/WorkstreamBreadcrumb';
import WorkstreamTaskActions from '../components/WorkstreamTaskActions';
import EditableTaskTitle from '../components/EditableTaskTitle';
import TaskStatusDropdown from '../components/TaskStatusDropdown';
import TaskAssignee from '../components/TaskAssignee';
import TaskEnvBranch from '../components/TaskEnvBranch';
import TaskRunExplainer from '../components/TaskRunExplainer';
import TaskSpec from '../components/TaskSpec';
import TaskModelSettings from '../components/TaskModelSettings';
import ParallelToggle from '../components/ParallelToggle';
import ActivitySection from '../components/ActivitySection';
import PermalinkCopyButton from '../components/PermalinkCopyButton';
import type { Task } from '../types/task';
import type { Workstream } from '../types/workstream';

const TASK_POLL_INTERVAL_MS = 5000;

export default function WorkstreamTaskPage() {
    const { taskID, workstreamID, shortNameAndTaskNum } = useParams();
    const [resolvedTaskID, setResolvedTaskID] = useState<string | undefined>();
    const [resolvedWorkstreamID, setResolvedWorkstreamID] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [task, setTask] = useState<Task | null>(null);
    const [workstream, setWorkstream] = useState<Workstream | null>(null);

    const effectiveTaskID = taskID ?? resolvedTaskID;
    const effectiveWorkstreamID = workstreamID ?? resolvedWorkstreamID;

    useEffect(() => {
        if (taskID) {
            setLoading(false);
            setError(undefined);
            setResolvedTaskID(undefined);
            setResolvedWorkstreamID(undefined);
            return;
        }
        if (!shortNameAndTaskNum) {
            setError('Invalid URL');
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(undefined);
        setResolvedTaskID(undefined);
        setResolvedWorkstreamID(undefined);
        fetch(`/api/workstreams/resolve/${encodeURIComponent(shortNameAndTaskNum)}`)
            .then(async (resp) => {
                if (cancelled) return;
                if (!resp.ok) {
                    const text = await resp.text();
                    if (cancelled) return;
                    setError(text || 'Failed to resolve task');
                    setLoading(false);
                    return;
                }
                const data: { taskID: string; workstreamID: string } = await resp.json();
                if (cancelled) return;
                setResolvedWorkstreamID(data.workstreamID);
                setResolvedTaskID(data.taskID);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || 'Failed to resolve task');
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [taskID, shortNameAndTaskNum]);

    const fetchTask = useCallback(async () => {
        if (!effectiveTaskID || !effectiveWorkstreamID) return;
        const wsQuery = `?workstreamID=${encodeURIComponent(effectiveWorkstreamID)}`;
        try {
            const resp = await fetch(`/api/tasks/${encodeURIComponent(effectiveTaskID)}${wsQuery}`);
            if (resp.ok) {
                const data: Task = await resp.json();
                setTask(data);
            }
        } catch {}
    }, [effectiveTaskID, effectiveWorkstreamID]);

    useEffect(() => {
        setTask(null);
        fetchTask();
    }, [fetchTask]);

    useEffect(() => {
        if (!effectiveTaskID || !effectiveWorkstreamID) {
            return;
        }
        const id = window.setInterval(() => {
            void fetchTask();
        }, TASK_POLL_INTERVAL_MS);
        return () => {
            window.clearInterval(id);
        };
    }, [effectiveTaskID, effectiveWorkstreamID, fetchTask]);

    const fetchWorkstream = useCallback(async () => {
        if (!effectiveWorkstreamID) return;
        try {
            const resp = await fetch(`/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}`);
            if (resp.ok) {
                const data: Workstream = await resp.json();
                setWorkstream(data);
            }
        } catch {}
    }, [effectiveWorkstreamID]);

    useEffect(() => {
        let cancelled = false;
        setWorkstream(null);
        fetch(`/api/workstreams/${encodeURIComponent(effectiveWorkstreamID ?? '')}`)
            .then(async (resp) => {
                if (cancelled || !resp.ok) return;
                const data: Workstream = await resp.json();
                if (!cancelled) setWorkstream(data);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [effectiveWorkstreamID]);

    const handleTitleSaved = useCallback((newTitle: string, newVersion: number) => {
        setTask((prev) => prev ? { ...prev, Title: newTitle, Version: newVersion } : prev);
    }, []);

    const handleStatusSaved = useCallback((updated: { State: string; Version: number; AssignedToAI: boolean }) => {
        setTask((prev) => prev ? {
            ...prev,
            State: updated.State,
            Version: updated.Version,
            AssignedToAI: updated.AssignedToAI,
        } : prev);
        fetchWorkstream();
    }, [fetchWorkstream]);

    const handleEnvBranchSaved = useCallback((updated: Record<string, any>) => {
        setTask((prev) => {
            if (!prev) return prev;
            const next = { ...prev, Version: updated.Version };
            if (updated.EnvironmentID !== undefined) next.EnvironmentID = updated.EnvironmentID;
            if (updated.RepoInfo !== undefined) next.RepoInfo = updated.RepoInfo;
            return next;
        });
    }, []);

    const handleSpecSaved = useCallback((newPrompt: string, newVersion: number) => {
        setTask((prev) => prev ? { ...prev, Prompt: newPrompt, Version: newVersion } : prev);
    }, []);

    const handleParallelSaved = useCallback((updated: { Parallel: boolean; Version: number }) => {
        setTask((prev) => prev ? { ...prev, Parallel: updated.Parallel, Version: updated.Version } : prev);
    }, []);

    const handleModelSettingsSaved = useCallback((updated: { ModelName: string; ReasoningLevel: string; Version: number }) => {
        setTask((prev) => prev ? {
            ...prev,
            ModelName: updated.ModelName,
            ReasoningLevel: updated.ReasoningLevel,
            Version: updated.Version,
        } : prev);
    }, []);

    const handleAssigneeSaved = useCallback((updated: { AssignedToAI: boolean; AssignedToTenantID: string; Version: number }) => {
        setTask((prev) => prev ? {
            ...prev,
            AssignedToAI: updated.AssignedToAI,
            AssignedToTenantID: updated.AssignedToTenantID,
            Version: updated.Version,
        } : prev);
        // Assigning to AI auto-pauses the workstream.
        fetchWorkstream();
    }, [fetchWorkstream]);

    if (loading) {
        return (
            <div style={{ paddingTop: '60px', maxWidth: '1024px', margin: '0 auto' }}>
                <p>Loading...</p>
            </div>
        );
    }

    if (error || !effectiveTaskID || !effectiveWorkstreamID) {
        return (
            <div style={{ paddingTop: '60px', maxWidth: '1024px', margin: '0 auto' }}>
                <p>{error || 'Task not found'}</p>
            </div>
        );
    }

    return (
        <div style={{ paddingTop: '20px' }}>
            {effectiveWorkstreamID && effectiveTaskID && (
                <>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 16px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <WorkstreamBreadcrumb
                                workstreamID={effectiveWorkstreamID}
                                taskID={effectiveTaskID}
                                workstream={workstream}
                                task={task}
                            />
                            {workstream && (
                                <PermalinkCopyButton
                                    url={`${window.location.origin}/workstreams/tasks/${encodeURIComponent(workstream.workstreamID)}/${encodeURIComponent(effectiveTaskID)}`}
                                />
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <WorkstreamTaskActions workstreamID={effectiveWorkstreamID} taskID={effectiveTaskID} task={task} onTaskChanged={fetchTask} />
                        </div>
                    </div>
                    {task && (
                        <div style={{ padding: '0 16px' }}>
                            <EditableTaskTitle
                                title={task.Title}
                                workstreamID={effectiveWorkstreamID}
                                taskID={effectiveTaskID}
                                version={task.Version ?? 1}
                                onSaved={handleTitleSaved}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                                <TaskStatusDropdown
                                    state={task.State ?? 'Pending'}
                                    assignedToAI={task.AssignedToAI ?? false}
                                    workstreamID={effectiveWorkstreamID}
                                    taskID={effectiveTaskID}
                                    version={task.Version ?? 1}
                                    onSaved={handleStatusSaved}
                                />
                                <TaskAssignee
                                    assignedToAI={task.AssignedToAI ?? false}
                                    assignedToTenantID={task.AssignedToTenantID ?? ''}
                                    workstreamID={effectiveWorkstreamID}
                                    taskID={effectiveTaskID}
                                    version={task.Version ?? 1}
                                    onSaved={handleAssigneeSaved}
                                />
                            </div>
                            {task.AssignedToAI && (
                                <>
                                    <TaskEnvBranch
                                        task={task}
                                        workstreamID={effectiveWorkstreamID}
                                        onSaved={handleEnvBranchSaved}
                                    />
                                    <TaskRunExplainer
                                        task={task}
                                        workstreamID={effectiveWorkstreamID}
                                        workstream={workstream}
                                    />
                                </>
                            )}
                            <TaskSpec
                                prompt={task.Prompt ?? ''}
                                workstreamID={effectiveWorkstreamID}
                                taskID={effectiveTaskID}
                                version={task.Version ?? 1}
                                onSaved={handleSpecSaved}
                            />
                            {task.AssignedToAI && (
                                <TaskModelSettings
                                    assignedToAI={task.AssignedToAI ?? false}
                                    modelName={task.ModelName}
                                    reasoningLevel={task.ReasoningLevel}
                                    workstreamID={effectiveWorkstreamID}
                                    taskID={effectiveTaskID}
                                    version={task.Version ?? 1}
                                    onSaved={handleModelSettingsSaved}
                                />
                            )}
                            <ParallelToggle
                                task={task}
                                workstreamID={effectiveWorkstreamID}
                                workstream={workstream}
                                onSaved={handleParallelSaved}
                            />
                            <ActivitySection
                                task={task}
                                workstreamID={effectiveWorkstreamID}
                                onTurnCreated={fetchTask}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
